import { DownloadSimpleIcon, PlusIcon } from '@phosphor-icons/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'

import { useExportMaintenanceCsv } from '@/api/maintenance/export-maintenance-csv'
import {
  GetMaintenanceRequestsRequest,
  useGetMaintenanceRequests,
} from '@/api/maintenance/get-maintenance-requests'
import { useGetUnit } from '@/api/units/get-unit'
import { TableGroup } from '@/components/layout/table-group'
import MaintenanceRequestPreview from '@/components/Maintenance/MaintenanceRequestPreview/MaintenanceRequestPreview'
import RequestMaintenanceForm from '@/components/Maintenance/RequestMaintenanceForm/RequestMaintenanceForm'
import ModalContainer from '@/components/ModalContainer/ModelContainer'
import { Button } from '@/components/ui/button'
import { DatePickerInput } from '@/components/ui/date-picker-input'
import { Icon } from '@/components/ui/icon'
import { StatusTabs } from '@/components/ui/status-tabs'
import { RowAction, SortOrder } from '@/components/ui/table'
import { Typography } from '@/components/ui/typography'
import { DEFAULT_TZ } from '@/constants/date'
import { toastFactory } from '@/factories'
import { useFilters, useSearchParams } from '@/hooks'
import { DateRange } from '@/models'
import { LockedResident, MaintenanceRequest, MaintenanceStatus } from '@/models/features'
import {
  getMaintenanceAllCount,
  getMaintenanceStatusCounts,
  getMaintenanceStatusTabs,
  MaintenanceStatusFilter,
} from '@/pages/maintenance/maintenance-dashboard/maintenance-dashboard.utils'
import { selectActiveBuilding } from '@/reducers/buildingSelectorReducer'
import { buildCsvFilename, downloadCsv, getApiErrorMessage } from '@/utils'
import { getLeaseHolder, getResidentFullName } from '@/utils/units'

import {
  getUnitMaintenanceColumns,
  UNIT_MAINTENANCE_TABLE_ID,
  unitMaintenanceFiltersSchema,
  unitMaintenanceInitialFilters,
} from './unit-maintenance.page.constants'

export const UnitMaintenancePage = () => {
  const { t } = useTranslation(['units'])
  const { t: tMaintenance } = useTranslation(['maintenance'])
  const { unitId } = useParams<{ unitId: string }>()
  const building = useSelector(selectActiveBuilding)
  const { data: unit } = useGetUnit({ unitId: unitId! })
  const timezone = building?.timezone || DEFAULT_TZ

  const [selectedRequest, setSelectedRequest] = useState<MaintenanceRequest | null>(null)
  const [isAddRequestOpen, setIsAddRequestOpen] = useState(false)

  const searchParams = useSearchParams({
    initialState: unitMaintenanceInitialFilters,
    schema: unitMaintenanceFiltersSchema,
  })

  const filters = useFilters({
    controlledState: searchParams.parsedQuery,
    initialState: unitMaintenanceInitialFilters,
    onFiltersChange: searchParams.writeParams,
  })

  const { limit, skip, sortBy, sortOrder, status = 'all', fromDate, toDate } = filters.filters

  const queryParams: GetMaintenanceRequestsRequest = {
    buildingId: building?.id,
    dateFrom: fromDate,
    dateTo: toDate,
    limit,
    skip,
    status: status === 'all' ? [] : [status],
    unitId: unitId ? Number(unitId) : undefined,
    updatedAtSort: sortOrder ? (sortOrder.toUpperCase() as 'ASC' | 'DESC') : undefined,
  }

  const { data, isFetching, error, refetch } = useGetMaintenanceRequests(queryParams)
  const requests = data?.maintenanceRequests ?? []
  const allCount = getMaintenanceAllCount(data)

  const statusCounts = getMaintenanceStatusCounts(data)

  const { mutateAsync: exportCsv, isPending: isExporting } = useExportMaintenanceCsv()

  // Note: the request form is resident-scoped, so for an occupied unit it is pre-filled with the
  // current lease holder; for a vacant unit the user picks the resident in the form.
  const leaseHolder = unit ? getLeaseHolder(unit.residents) : null
  const lockedResident: LockedResident | undefined =
    unit && leaseHolder?.clientId
      ? {
          apartment: Number(unit.unit) || null,
          buildingNumber: unit.buildingNumber,
          email: leaseHolder.email ?? undefined,
          id: leaseHolder.clientId,
          name: getResidentFullName(leaseHolder),
        }
      : undefined

  const rowActions: RowAction<MaintenanceRequest>[] = [
    {
      label: tMaintenance('maintenance:requests.rowActions.view'),
      onClick: (row) => setSelectedRequest(row),
      primary: true,
    },
  ]

  const dateRange: DateRange = [
    fromDate ? new Date(fromDate) : null,
    toDate ? new Date(toDate) : null,
  ]

  const handleDownloadCSV = async () => {
    if (!building) return

    try {
      const blob = await exportCsv({ ...queryParams, buildingId: building.id })
      downloadCsv({
        blob,
        filename: buildCsvFilename({
          building: building.name,
          dateRange,
          page: 'maintenance',
          timezone,
        }),
      })
    } catch (downloadError) {
      toastFactory.error(getApiErrorMessage(downloadError))
    }
  }

  const handleSort = (_sortBy: string | null, newSortOrder: SortOrder | null) => {
    filters.setFilters({
      sortBy: _sortBy ?? undefined,
      sortOrder: newSortOrder ?? undefined,
    })
  }

  const handleCloseRequest = () => {
    setSelectedRequest(null)
    void refetch()
  }

  if (selectedRequest) {
    return (
      <MaintenanceRequestPreview
        data={selectedRequest}
        buildingData={building}
        handleClose={handleCloseRequest}
      />
    )
  }

  const statusTabs = getMaintenanceStatusTabs(tMaintenance, statusCounts, allCount)

  return (
    <>
      <TableGroup
        noShadow
        id={UNIT_MAINTENANCE_TABLE_ID}
        columns={getUnitMaintenanceColumns({ t: tMaintenance, timezone })}
        data={requests}
        error={error}
        getRowKey={(request) => request.id}
        loading={!building || isFetching}
        rowActions={rowActions}
        title={
          <Typography as="h2" size="heading-small" color="black800">
            {t('units:maintenance.title')}
          </Typography>
        }
        titleActions={
          <Button size="small" onClick={() => setIsAddRequestOpen(true)}>
            {tMaintenance('maintenance:toolbar.addRequest')}
            <Icon icon={PlusIcon} />
          </Button>
        }
        emptyNoFilters={{
          description: t('units:maintenance.empty.description'),
          title: t('units:maintenance.empty.title'),
        }}
        navigation={{
          actions: (
            <Button
              onlyBox
              size="small"
              use="secondary"
              title={tMaintenance('maintenance:requests.csv')}
              disabled={isExporting || !requests.length}
              onClick={handleDownloadCSV}
            >
              <Icon icon={DownloadSimpleIcon} />
            </Button>
          ),
          datePicker: (
            <DatePickerInput
              mode="range"
              placeholder={tMaintenance('maintenance:requests.dateRangePlaceholder')}
              size="small"
              value={dateRange}
              onRangeChange={(range: DateRange) =>
                filters.setFilters({ fromDate: range[0]?.getTime(), toDate: range[1]?.getTime() })
              }
            />
          ),
          statusTabs: (
            <StatusTabs
              options={statusTabs}
              value={status}
              onChange={(value: MaintenanceStatusFilter) => filters.setFilter('status', value)}
            />
          ),
        }}
        pagination={{
          paginationFilters: filters,
          // Note: without per-status counts (a BE before EL-15063) page by the overall total - see
          // the Maintenance dashboard.
          total: status === 'all' ? allCount : (statusCounts[status] ?? allCount),
        }}
        tableFilters={filters}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={handleSort}
      />
      {isAddRequestOpen && (
        <ModalContainer
          headerTitle={tMaintenance('maintenance:page.createRequestModalTitle')}
          onClose={() => setIsAddRequestOpen(false)}
        >
          <RequestMaintenanceForm
            toggleModal={() => setIsAddRequestOpen(false)}
            lockResident={lockedResident}
            lockBuilding={building}
            onSuccess={() => void refetch()}
          />
        </ModalContainer>
      )}
    </>
  )
}
